import React, { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { Box } from '~Styles';
import { useI18n, useSelector } from '~Hooks';
import { CompleteBongQuery_venue_company_items } from '~Scenes/POS/Queries/__queries__';
import ProductRow from './ProductRow';

const ITEMS_PER_PAGE = 30;

interface Props {
  items: CompleteBongQuery_venue_company_items[];
  search?: string;
}

const Products: FC<Props> = ({ items, search = '' }) => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId) as string;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Filter and sort products alphabetically by translated title
  const sortedItems = useMemo(() => {
    const query = search.toLowerCase();
    return [...items]
      .filter(
        (item) =>
          !query ||
          i18n(item.titleLang)
            .toLowerCase()
            .includes(query),
      )
      .sort((a, b) => {
        const nameA = i18n(a.titleLang).toLowerCase();
        const nameB = i18n(b.titleLang).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [items, i18n, search]);

  // Check if at least one item has a costBasis value set
  const hasCostBasis = useMemo(() => items.some((item) => item.costBasis != null), [items]);

  // Reset visible count when search changes
  const prevSearchRef = useRef(search);
  if (prevSearchRef.current !== search) {
    prevSearchRef.current = search;
    setVisibleCount(ITEMS_PER_PAGE);
  }

  const visibleItems = sortedItems.slice(0, visibleCount);
  const hasMore = visibleCount < sortedItems.length;

  // Intersection observer to load more items when scrolling near the bottom
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node || !hasMore) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
          }
        },
        { threshold: 0.1 },
      );

      observerRef.current.observe(node);
    },
    [hasMore],
  );

  return (
    <Box>
      {visibleItems.map((item) => (
        <ProductRow key={item.id} item={item} venueId={venueId} hasCostBasis={hasCostBasis} />
      ))}
      {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
    </Box>
  );
};

export default memo(Products);
